import type { TableData } from "$lib/table/tablelogic/tablelogic.svelte";
import type { SelectedCellsRange } from "$lib/types";
import type { ColumnConditions } from "handsontable/plugins/filters";
import { writable, get } from "svelte/store";

export interface CollaboratorSelection {
  hue: number;
  row: number;
  column: number;
  row2: number;
  column2: number;
  userEmail: string;
  picture: string;
  name: string;
}

export interface TableStore {
  tableData: TableData;
  currentTable: {
    id: string;
    name: string;
    visibility: "private" | "public" | "organization";
    accessLevel: "owner" | "editor" | "view";
    publicPermission: "view" | "edit" | "none";
    teamPermission: "view" | "edit" | "none";
  };
  addDataSheetOpen: boolean;
  isChatOpen: boolean;
  currentViewId: string | "main";
  currentViewFilters: ColumnConditions[];
  chatCellsRange: SelectedCellsRange[];
  addTemplateOpen: boolean;
  addDataType: "amazon" | "google" | "shopify" | "custom" | null;
  formOpen: boolean;
  showSubscriptionDialog: boolean;
  showSubscriptionDialogData: {
    title: string;
    description: string;
  };
  showCreateTableDialog: boolean;
  selectedCellId: string | null;
  selectedRow: number | null;
  devMode: boolean;
  isSidebarOpen: boolean;
  isTableRightSidebarOpen: boolean;
  isFileViewerOpen: boolean;
  fileId: string;
  fileName: string;
  collaboratorOrderCounter: number;
  collaboratorSelectionOrder: Record<string, number>;
  collaboratorCellSelections: Record<string, CollaboratorSelection>;
  searchQuery: string;
  searchResultCount: number;
  currentPdfCellId: string | null;
  isInShortcutMode: boolean;
  currentShortcutSequence: string;
  editingName: {
    id: string;
    val: string;
  };
  rowHeightSettings: Record<string, "short" | "medium" | "tall" | "extra-tall">;
  focusTarget: FocusTarget | null;
}

export type FocusTarget = {
  type: "name" | "systemPrompt" | "userPrompt" | "model" | "agent";
  columnId: string;
};

const STORAGE_KEY = "tableRowHeightSettings";

function createTableStore() {
  const initialState: TableStore = {
    selectedCellId: null,
    selectedRow: null,
    formOpen: false,
    currentViewId: "main",
    chatCellsRange: [],
    currentViewFilters: [],
    isChatOpen: false,
    showSubscriptionDialog: false,
    showSubscriptionDialogData: {
      title: "",
      description: "",
    },
    showCreateTableDialog: false,
    addDataSheetOpen: false,
    addTemplateOpen: false,
    addDataType: null,
    tableData: { columns: [], cells: [] },
    currentTable: {
      id: "",
      name: "",
      visibility: "organization",
      publicPermission: "none",
      teamPermission: "none",
      accessLevel: "owner",
    },
    devMode: false,
    isSidebarOpen: true,
    isTableRightSidebarOpen: true,
    isFileViewerOpen: false,
    fileId: "",
    fileName: "",
    collaboratorOrderCounter: 0,
    collaboratorSelectionOrder: {},
    collaboratorCellSelections: {},
    searchQuery: "",
    searchResultCount: 0,
    currentPdfCellId: null,
    isInShortcutMode: false,
    currentShortcutSequence: "",
    editingName: {
      id: "",
      val: "",
    },
    rowHeightSettings: (() => {
      if (typeof window !== "undefined") {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
      }
      return {};
    })(),
    focusTarget: null as FocusTarget | null,
  };

  const { subscribe, set, update } = writable<TableStore>(initialState);

  return {
    subscribe,

    getState: (): TableStore => {
      return get({ subscribe });
    },

    setCurrentViewId: (viewId: string | "main") => {
      update((state) => ({
        ...state,
        currentViewId: viewId,
      }));
    },

    setIsChatOpen: (value: boolean) => {
      update((state) => ({
        ...state,
        isChatOpen: value,
      }));
    },

    setChatCellsRange: (range: SelectedCellsRange[]) => {
      update((state) => ({
        ...state,
        chatCellsRange: range,
      }));
    },

    // Table Actions
    setCurrentTable: (table: TableStore["currentTable"]) => {
      update((state) => ({
        ...state,
        currentTable: table,
      }));
    },

    setTableData: (tableData: TableData) => {
      update((state) => ({
        ...state,
        tableData,
      }));
    },

    // UI Actions
    setAddTemplateOpen: (value: boolean) => {
      update((state) => ({
        ...state,
        addTemplateOpen: value,
      }));
    },

    setShowCreateTableDialog: (value: boolean) => {
      update((state) => ({
        ...state,
        showCreateTableDialog: value,
      }));
    },

    setAddDataSheetOpen: (value: boolean) => {
      update((state) => ({
        ...state,
        addDataSheetOpen: value,
      }));
    },

    setFormOpen: (value: boolean) => {
      update((state) => ({
        ...state,
        formOpen: value,
      }));
    },

    setAddDataType: (type: "amazon" | "google" | "shopify" | "custom" | null) => {
      update((state) => ({
        ...state,
        addDataType: type,
      }));
    },

    toggleSidebar: () => {
      update((state) => ({
        ...state,
        isSidebarOpen: state.currentTable.accessLevel === "view" ? false : !state.isSidebarOpen,
      }));
    },

    // File Actions
    toggleFileViewer: (value?: boolean) => {
      update((state) => ({
        ...state,
        isFileViewerOpen: value ?? !state.isFileViewerOpen,
        selectedRow: null,
        selectedCellId: null,
      }));
    },

    setFileId: (fileId: string) => {
      update((state) => ({
        ...state,
        fileId,
      }));
    },

    setFileName: (fileName: string) => {
      update((state) => ({
        ...state,
        fileName,
      }));
    },

    // Collaborator Actions
    updateCollaboratorSelection: (collaboratorId: string, selection: CollaboratorSelection | null) => {
      update((state) => {
        const newSelections = { ...state.collaboratorCellSelections };
        const newOrder = { ...state.collaboratorSelectionOrder };

        if (selection) {
          if (!(collaboratorId in newOrder)) {
            newOrder[collaboratorId] = state.collaboratorOrderCounter;
          }
          newSelections[collaboratorId] = {
            ...selection,
            userEmail: selection.userEmail || "Anonymous User",
            name: selection.name || "Anonymous User",
          };
        } else {
          delete newSelections[collaboratorId];
        }

        return {
          ...state,
          collaboratorCellSelections: newSelections,
          collaboratorSelectionOrder: newOrder,
          collaboratorOrderCounter:
            selection && !(collaboratorId in newOrder)
              ? state.collaboratorOrderCounter + 1
              : state.collaboratorOrderCounter,
        };
      });
    },

    cleanupCollaborator: (collaboratorId: string) => {
      update((state) => {
        const newSelections = { ...state.collaboratorCellSelections };
        const newOrder = { ...state.collaboratorSelectionOrder };

        delete newSelections[collaboratorId];
        delete newOrder[collaboratorId];

        return {
          ...state,
          collaboratorCellSelections: newSelections,
          collaboratorSelectionOrder: newOrder,
        };
      });
    },

    clearAllCollaborators: () => {
      update((state) => ({
        ...state,
        collaboratorOrderCounter: 0,
        collaboratorSelectionOrder: {},
        collaboratorCellSelections: {},
      }));
    },

    setSelectedCellId: (cellId: string | null) => {
      update((state) => ({
        ...state,
        selectedCellId: cellId,
        selectedRow: null,
        isFileViewerOpen: false,
      }));
    },

    setSelectedRow: (row: number | null) => {
      update((state) => ({
        ...state,
        selectedRow: row,
        isFileViewerOpen: false,
        currentPdfCellId: null,
        selectedCellId: null,
      }));
    },

    // Search Actions
    setSearchQuery: (query: string) => {
      update((state) => ({
        ...state,
        searchQuery: query,
      }));
    },

    setSearchResultCount: (count: number) => {
      update((state) => ({
        ...state,
        searchResultCount: count,
      }));
    },

    // PDF Actions
    setCurrentPdfCellId: (cellId: string | null) => {
      update((state) => ({
        ...state,
        currentPdfCellId: cellId,
      }));
    },

    // Shortcut Actions
    setShortcutMode: (isEnabled: boolean) => {
      update((state) => ({
        ...state,
        isInShortcutMode: isEnabled,
        currentShortcutSequence: isEnabled ? "" : state.currentShortcutSequence,
      }));
    },

    setShortcutSequence: (sequence: string) => {
      update((state) => ({
        ...state,
        currentShortcutSequence: sequence,
      }));
    },

    clearShortcutSequence: () => {
      update((state) => ({
        ...state,
        currentShortcutSequence: "",
      }));
    },

    resetShortcutState: () => {
      update((state) => ({
        ...state,
        isInShortcutMode: false,
        currentShortcutSequence: "",
      }));
    },

    setRowHeight: (tableId: string, height: "short" | "medium" | "tall" | "extra-tall") => {
      update((state) => {
        const newSettings = {
          ...state.rowHeightSettings,
          [tableId]: height,
        };

        if (typeof window !== "undefined") {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
        }

        return {
          ...state,
          rowHeightSettings: newSettings,
        };
      });
    },

    // Other Actions
    setDevMode: (value: boolean) => {
      update((state) => ({
        ...state,
        devMode: value,
      }));
    },

    setEditingTableName: ({ id, val }: { id: string; val: string }) => {
      update((state) => ({
        ...state,
        editingName: {
          id,
          val,
        },
      }));
    },

    setShowSubscriptionDialog: (value: boolean, data?: { title: string; description: string }) => {
      update((state) => ({
        ...state,
        showSubscriptionDialog: value,
        showSubscriptionDialogData: data ?? {
          title: "",
          description: "",
        },
      }));
    },

    setTableRightSidebarOpen: (value: boolean) => {
      update((state) => ({
        ...state,
        isTableRightSidebarOpen: value,
      }));
    },

    setFocusTarget: (target: FocusTarget | null) => {
      update((state) => ({
        ...state,
        focusTarget: target,
      }));
    },

    setCurrentViewFilters: (filters: ColumnConditions[]) => {
      update((state) => ({
        ...state,
        currentViewFilters: filters,
      }));
    },

    clearCurrentViewFilters: () => {
      update((state) => ({
        ...state,
        currentViewFilters: [],
      }));
    },
  };
}

export const tableStore = createTableStore();
